<script setup>
import { ref, nextTick, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import Spinner from 'next/spinner/Spinner.vue';
import WootMessageEditor from 'components/widgets/WootWriter/Editor.vue';
import ReportChatMessage from './ReportChatMessage.vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isResetting: {
    type: Boolean,
    default: false,
  },
  showReset: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['send', 'reset']);

const { t } = useI18n();

const inputMessage = ref('');
const chatContainer = ref(null);

const hasMessages = computed(() => props.messages.length > 0);

const computedPlaceholder = computed(
  () =>
    props.placeholder ||
    (hasMessages.value
      ? t('WGPT_CUSTOM_REPORTS.CHAT.FOLLOWUP_PLACEHOLDER')
      : t('WGPT_CUSTOM_REPORTS.CHAT.INITIAL_PLACEHOLDER'))
);

async function scrollToBottom() {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
}

function sendMessage() {
  const text = inputMessage.value && inputMessage.value.trim();
  if (!text || props.isLoading) return;
  emit('send', text);
  inputMessage.value = '';
  scrollToBottom();
}

useKeyboardEvents({
  '$mod+Enter': {
    action: sendMessage,
    allowOnFocusedInput: true,
  },
});

defineExpose({ scrollToBottom });
</script>

<template>
  <div class="flex flex-col justify-between flex-grow h-full min-w-0 m-0">
    <div
      v-if="showReset && hasMessages"
      class="flex justify-end px-3 pt-3 pb-0 shrink-0"
    >
      <Button
        :label="t('WGPT_CUSTOM_REPORTS.CHAT.RESET')"
        icon="i-lucide-rotate-ccw"
        variant="ghost"
        size="xs"
        color="slate"
        :is-loading="isResetting"
        @click="emit('reset')"
      />
    </div>

    <div
      ref="chatContainer"
      class="flex-shrink min-h-0 flex-grow basis-px flex flex-col overflow-y-auto h-full m-0 pt-8 pb-4 px-3"
    >
      <div
        v-if="!hasMessages && !isLoading"
        class="my-auto flex flex-col items-center gap-2 text-center px-2"
      >
        <span class="i-lucide-messages-square text-n-slate-11 size-10 mb-2" />
        <h1 class="text-xl">
          {{ t('WGPT_CUSTOM_REPORTS.CHAT.EMPTY_TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-10">
          {{ t('WGPT_CUSTOM_REPORTS.CHAT.EMPTY_DESCRIPTION') }}
        </p>
      </div>

      <ReportChatMessage
        v-for="(msg, index) in messages"
        :key="index"
        :message="msg"
      />

      <div v-if="isLoading" class="flex justify-start mb-3">
        <div
          class="bg-n-surface-2 rounded-xl px-4 py-3 flex items-center gap-2"
        >
          <Spinner :size="16" />
          <span class="text-sm text-n-slate-11">
            {{ t('WGPT_CUSTOM_REPORTS.CHAT.THINKING') }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="relative rounded-xl rounded-t-none border-t border-n-weak bg-n-solid-1 pb-4"
    >
      <div class="w-full px-4 min-h-11">
        <WootMessageEditor
          v-model="inputMessage"
          editor-id="wgpt-custom-reports-chat"
          class="input popover-prosemirror-menu [&_.ProseMirror-woot-style]:min-h-[2rem]"
          dir="auto"
          :placeholder="computedPlaceholder"
          :min-height="1"
        />
      </div>
      <div class="flex justify-end p-3">
        <Button
          :label="t('WGPT_CUSTOM_REPORTS.CHAT.SEND')"
          type="submit"
          sm
          color="blue"
          :disabled="!inputMessage || isLoading"
          class="flex-shrink-0"
          @click="sendMessage"
        />
      </div>
    </div>
  </div>
</template>
